import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  CheckOutlined,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  ProTable,
  type ActionType,
  type ProColumns,
} from '@ant-design/pro-components';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CatalogAlias = {
  id: string;
  alias: string;
  aliasType: string;
  sourceName: string | null;
  sourceRecordId: string | null;
  isPreferred: boolean;
};

type CatalogEntry = {
  id: string;
  code: string;
  catalogTier: 'CORE' | 'EXTENDED' | 'REFERENCE';
  canonicalAccession: string | null;
  nameZh: string | null;
  nameEn: string | null;
  species: string;
  originType: string;
  tissueOrigin: string | null;
  cellCategory: string | null;
  disease: string | null;
  growthMode: string | null;
  cultureMedium: string | null;
  serum: string | null;
  antibiotics: string | null;
  cultureConditions: string | null;
  passageRatio: string | null;
  doublingTime: string | null;
  biosafetyLevel: string | null;
  cultureSources: Record<
    string,
    Array<{
      value?: string;
      sourceName?: string;
      sourceVersion?: string;
      sourceUrl?: string;
      confidence?: string;
      note?: string;
    }>
  > | null;
  sourceName: string;
  sourceRecordId: string | null;
  sourceUrl: string | null;
  sourceVersion: string | null;
  license: string | null;
  riskStatus: string;
  riskNote: string | null;
  curationStatus: 'DRAFT' | 'APPROVED' | 'RETIRED';
  aiStatus:
    | 'IMPORTED'
    | 'AI_PASSED'
    | 'AI_WARNING'
    | 'REVIEW_REQUIRED'
    | 'REJECTED'
    | 'PUBLISHED'
    | 'HIDDEN'
    | 'RETIRED';
  publishedAt: string | null;
  lastEvaluatedAt: string | null;
  lastRuleVersion: string | null;
  inputHash: string | null;
  sourceImportedAt: string | null;
  enabledByDefault: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  version: number;
  enabled: boolean;
  aliases: CatalogAlias[];
  updatedAt: string;
};

type CatalogStats = {
  total: number;
  core: number;
  extended: number;
  reviewRequired: number;
  retired: number;
  published: number;
};

type CatalogFilterOptions = {
  catalogTier: string[];
  species: string[];
  sourceName: string[];
  aiStatus: string[];
  riskStatus: string[];
  curationStatus: string[];
  status: string[];
  enabled: string[];
};

type VersionRow = {
  id: string;
  version: number;
  snapshot: Record<string, unknown>;
  changeSummary: string | null;
  sourceName: string | null;
  sourceVersion: string | null;
  inputHash: string | null;
  createdAt: string;
};

type EvaluationRow = {
  id: string;
  sourceVersion: string | null;
  inputHash: string | null;
  ruleVersion: string;
  aiStatus: string;
  evidence: { evidenceSummary?: string };
  findings: Array<{
    code: string;
    severity: string;
    messageZh: string;
    messageEn: string;
    evidence: string;
  }>;
  riskReasons: string[];
  publishedAllowed: boolean;
  evaluatedAt: string;
  createdAt: string;
};

type AuditRow = {
  eventType: string;
  result: string;
  actorId: string | null;
  objectDisplayNo: string | null;
  afterData: unknown;
  createdAt: string;
};

type CatalogDetail = CatalogEntry & {
  versions: VersionRow[];
  evaluations: EvaluationRow[];
  auditRecords: AuditRow[];
};

type DiffChange = {
  field: string;
  before: string;
  after: string;
};

type CatalogFormValues = Omit<
  CatalogEntry,
  | 'id'
  | 'version'
  | 'enabled'
  | 'aliases'
  | 'curationStatus'
  | 'aiStatus'
  | 'publishedAt'
  | 'lastEvaluatedAt'
  | 'lastRuleVersion'
  | 'inputHash'
  | 'sourceImportedAt'
  | 'updatedAt'
> & {
  curationStatus: CatalogEntry['curationStatus'];
  aliasesText: string;
};

const options = (values: string[], t: (key: string) => string) =>
  values.map((value) => ({
    value,
    label: t(`admin.catalogOptions.${value}`),
  }));

const aiStatusColor: Record<string, string> = {
  IMPORTED: 'default',
  AI_PASSED: 'green',
  AI_WARNING: 'gold',
  REVIEW_REQUIRED: 'orange',
  REJECTED: 'red',
  PUBLISHED: 'blue',
  HIDDEN: 'default',
  RETIRED: 'default',
};

const RECOMMENDED_CULTURE_SOURCE = '__recommended__';
const CULTURE_FIELD_KEYS = [
  'growthMode',
  'cultureMedium',
  'serum',
  'cultureConditions',
  'passageRatio',
  'doublingTime',
  'biosafetyLevel',
] as const;
export function CellLineCatalogAdminPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<CatalogEntry>();
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState<CatalogDetail>();
  const [detailOpen, setDetailOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffFrom, setDiffFrom] = useState<number>();
  const [diffTo, setDiffTo] = useState<number>();
  const [diffChanges, setDiffChanges] = useState<DiffChange[]>([]);
  const [activeCultureSource, setActiveCultureSource] = useState<string>();
  const [stats, setStats] = useState<CatalogStats>();
  const [filterOptions, setFilterOptions] = useState<CatalogFilterOptions>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [form] = Form.useForm<CatalogFormValues>();
  const [messageApi, holder] = message.useMessage();
  const actionRef = useRef<ActionType | undefined>(undefined);

  const requestCatalog = async (params: Record<string, unknown>) => {
    try {
      const search = new URLSearchParams();
      for (const key of [
        'query',
        'catalogTier',
        'species',
        'sourceName',
        'sourceVersion',
        'riskStatus',
        'curationStatus',
        'aiStatus',
        'status',
        'enabled',
      ]) {
        const value = params[key];
        if (typeof value === 'string' && value.trim()) search.set(key, value);
        if (typeof value === 'boolean') search.set(key, String(value));
      }
      search.set('page', String(Number(params.current ?? 1)));
      search.set('pageSize', String(Number(params.pageSize ?? 20)));
      const response = await fetch(
        `/api/v1/admin/cell-line-catalog?${search.toString()}`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error(t('admin.catalogLoadFailed'));
      const payload = (await response.json()) as {
        items: CatalogEntry[];
        total: number;
        stats?: CatalogStats;
        filterOptions?: CatalogFilterOptions;
      };
      if (payload.stats) setStats(payload.stats);
      if (payload.filterOptions) setFilterOptions(payload.filterOptions);
      return { data: payload.items, total: payload.total, success: true };
    } catch (error) {
      console.error(error);
      await messageApi.error(t('admin.catalogLoadFailed'));
      return { data: [], total: 0, success: false };
    }
  };

  const displayName = (entry: CatalogEntry) =>
    i18n.resolvedLanguage === 'en-US'
      ? (entry.nameEn ?? entry.nameZh ?? entry.code)
      : (entry.nameZh ?? entry.nameEn ?? entry.code);

  const openCreate = () => {
    setEditing(undefined);
    form.resetFields();
    form.setFieldsValue({
      originType: 'COMMERCIAL',
      catalogTier: 'EXTENDED',
      riskStatus: 'REVIEW',
      curationStatus: 'DRAFT',
      status: 'ACTIVE',
      enabledByDefault: false,
      aliasesText: '',
    } as CatalogFormValues);
    setModalOpen(true);
  };

  const openEdit = (entry: CatalogEntry) => {
    setEditing(entry);
    form.setFieldsValue({
      ...entry,
      aliasesText: entry.aliases.map((alias) => alias.alias).join('\n'),
    });
    setModalOpen(true);
  };

  const toPayload = (values: CatalogFormValues) => ({
    ...values,
    aliases: values.aliasesText
      .split(/[\n,，]/)
      .map((alias) => alias.trim())
      .filter(Boolean)
      .map((alias) => ({
        alias,
        aliasType: alias.toUpperCase().startsWith('CVCL') ? 'CVCL' : 'NAME',
      })),
  });

  const save = async (values: CatalogFormValues) => {
    const response = await fetch(
      `/api/v1/admin/cell-line-catalog${editing ? `/${editing.id}` : ''}`,
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...toPayload(values),
          ...(editing ? { version: editing.version } : {}),
        }),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('admin.catalogSaveFailed'));
      return;
    }
    setModalOpen(false);
    void actionRef.current?.reload();
    await messageApi.success(t('admin.catalogSaved'));
  };

  const updateCuration = async (
    entry: CatalogEntry,
    curationStatus: CatalogEntry['curationStatus'],
  ) => {
    const response = await fetch(
      `/api/v1/admin/cell-line-catalog/${entry.id}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...entry,
          curationStatus,
          aliases: entry.aliases.map((alias) => ({
            alias: alias.alias,
            aliasType: alias.aliasType,
            sourceName: alias.sourceName,
            sourceRecordId: alias.sourceRecordId,
            isPreferred: alias.isPreferred,
          })),
        }),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('admin.catalogSaveFailed'));
      return;
    }
    void actionRef.current?.reload();
    if (detail?.id === entry.id) await openDetail(entry.id);
    await messageApi.success(t('admin.catalogSaved'));
  };

  const catalogAction = async (
    entry: CatalogEntry,
    action: 'publish' | 'hide' | 'retire',
  ) => {
    const response = await fetch(
      `/api/v1/admin/cell-line-catalog/${entry.id}/${action}`,
      {
        method: 'POST',
        credentials: 'include',
      },
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        message?: string;
      } | null;
      await messageApi.error(payload?.message ?? t('admin.catalogSaveFailed'));
      return;
    }
    void actionRef.current?.reload();
    if (detail?.id === entry.id) await openDetail(entry.id);
    await messageApi.success(t(`admin.catalogActionDone.${action}`));
  };

  const openDetail = async (id: string) => {
    setDetailOpen(true);
    setActiveCultureSource(undefined);
    try {
      const response = await fetch(
        `/api/v1/admin/cell-line-catalog/${id}`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error(t('admin.catalogLoadFailed'));
      setDetail((await response.json()) as CatalogDetail);
    } catch (error) {
      console.error(error);
      await messageApi.error(t('admin.catalogLoadFailed'));
    }
  };

  const batchAction = async (
    action: 'publish' | 'retire',
    ids: string[],
  ) => {
    const response = await fetch(
      `/api/v1/admin/cell-line-catalog/batch-${action}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ids }),
      },
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        message?: string;
      } | null;
      await messageApi.error(payload?.message ?? t('admin.catalogSaveFailed'));
      return;
    }
    const payload = (await response.json().catch(() => null)) as {
      published?: number;
      retired?: number;
      skipped?: string[];
    } | null;
    setSelectedRowKeys([]);
    void actionRef.current?.reload();
    if (action === 'publish') {
      await messageApi.success(
        t('admin.catalogBatchPublished', {
          count: payload?.published ?? 0,
          skipped: payload?.skipped?.length ?? 0,
        }),
      );
    } else {
      await messageApi.success(
        t('admin.catalogBatchRetired', {
          count: payload?.retired ?? 0,
          skipped: payload?.skipped?.length ?? 0,
        }),
      );
    }
  };

  const showDiff = async () => {
    if (!detail || !diffFrom || !diffTo || diffFrom === diffTo) return;
    const response = await fetch(
      `/api/v1/admin/cell-line-catalog/${detail.id}/diff?from=${diffFrom}&to=${diffTo}`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      await messageApi.error(t('admin.catalogDiffFailed'));
      return;
    }
    const payload = (await response.json()) as { changes: DiffChange[] };
    setDiffChanges(payload.changes);
  };

  const aiStatusLabel = (status: string) => {
    const label = t(`admin.catalogAiStatus.${status}`, {
      defaultValue: status,
    });
    return label;
  };

  const publishBlocked = (entry: CatalogEntry) =>
    entry.aiStatus === 'REVIEW_REQUIRED' ||
    entry.aiStatus === 'REJECTED' ||
    entry.aiStatus === 'IMPORTED';

  const existingEnum = (values: string[] | undefined) =>
    Object.fromEntries(
      (values ?? []).map((value) => [value, { text: value }]),
    );
  const labeledEnum = (
    values: string[] | undefined,
    label: (value: string) => string,
  ) =>
    Object.fromEntries(
      (values ?? []).map((value) => [value, { text: label(value) }]),
    );

  const columns: ProColumns<CatalogEntry>[] = [
    {
      title: t('admin.catalogSearchCellLine'),
      key: 'cellLineSearch',
      hideInTable: true,
      search: {
        transform: (value: string) => ({ query: value }),
      },
      fieldProps: {
        placeholder: t('admin.catalogSearchCellLinePlaceholder'),
      },
    },
    {
      title: (
        <Tooltip title={t('admin.catalogCodeHint')}>
          {t('admin.catalogCode')}
        </Tooltip>
      ),
      dataIndex: 'code',
      width: 150,
      fixed: 'left',
      search: false,
      render: (_, entry) => entry.code,
    },
    {
      title: t('admin.catalogName'),
      width: 180,
      search: false,
      render: (_, entry) => displayName(entry),
    },
    {
      title: t('admin.catalogAccession'),
      dataIndex: 'canonicalAccession',
      width: 140,
      search: false,
    },
    {
      title: t('admin.catalogTier'),
      dataIndex: 'catalogTier',
      width: 110,
      valueType: 'select',
      valueEnum: labeledEnum(
        filterOptions?.catalogTier,
        (value) => t(`admin.catalogOptions.${value}`),
      ),
      render: (_, entry) => (
        <Tag color={entry.catalogTier === 'CORE' ? 'blue' : 'default'}>
          {t(`admin.catalogOptions.${entry.catalogTier}`)}
        </Tag>
      ),
    },
    {
      title: t('admin.catalogSpecies'),
      dataIndex: 'species',
      width: 140,
      valueType: 'select',
      valueEnum: existingEnum(filterOptions?.species),
    },
    {
      title: t('admin.catalogSource'),
      dataIndex: 'sourceName',
      width: 140,
      valueType: 'select',
      valueEnum: existingEnum(filterOptions?.sourceName),
    },
    {
      title: t('admin.catalogSourceVersion'),
      dataIndex: 'sourceVersion',
      width: 140,
      search: false,
    },
    {
      title: (
        <Tooltip title={t('admin.catalogAiStatusHint')}>
          {t('admin.catalogAiStatusTitle')}
        </Tooltip>
      ),
      dataIndex: 'aiStatus',
      width: 130,
      valueType: 'select',
      valueEnum: labeledEnum(
        filterOptions?.aiStatus,
        (value) => t(`admin.catalogAiStatus.${value}`),
      ),
      render: (_, entry) => (
        <Tag color={aiStatusColor[entry.aiStatus] ?? 'default'}>
          {aiStatusLabel(entry.aiStatus)}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title={t('admin.catalogRiskHint')}>
          {t('admin.catalogRisk')}
        </Tooltip>
      ),
      dataIndex: 'riskStatus',
      width: 130,
      valueType: 'select',
      valueEnum: labeledEnum(
        filterOptions?.riskStatus,
        (value) => t(`admin.catalogOptions.${value}`),
      ),
      render: (_, entry) => (
        <Tag color={entry.riskStatus === 'CLEAR' ? 'green' : 'orange'}>
          {t(`admin.catalogOptions.${entry.riskStatus}`)}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title={t('admin.catalogCurationHint')}>
          {t('admin.catalogCuration')}
        </Tooltip>
      ),
      dataIndex: 'curationStatus',
      width: 110,
      valueType: 'select',
      valueEnum: labeledEnum(
        filterOptions?.curationStatus,
        (value) => t(`admin.catalogOptions.${value}`),
      ),
      render: (_, entry) => (
        <Tag
          color={
            entry.curationStatus === 'APPROVED'
              ? 'green'
              : entry.curationStatus === 'RETIRED'
                ? 'default'
                : 'gold'
          }
        >
          {t(`admin.catalogOptions.${entry.curationStatus}`)}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title={t('admin.catalogRecordStatusHint')}>
          {t('admin.catalogRecordStatus')}
        </Tooltip>
      ),
      dataIndex: 'status',
      width: 100,
      valueType: 'select',
      valueEnum: labeledEnum(
        filterOptions?.status,
        (value) => t(`admin.catalogOptions.${value}`),
      ),
      render: (_, entry) => (
        <Tag color={entry.status === 'ACTIVE' ? 'green' : 'default'}>
          {t(`admin.catalogOptions.${entry.status}`)}
        </Tag>
      ),
    },
    {
      title: t('admin.catalogEnabledStatus'),
      dataIndex: 'enabled',
      width: 100,
      search: false,
      render: (_, entry) =>
        entry.enabled ? (
          <Tag color="green">{t('common.enabled')}</Tag>
        ) : (
          <Tag>{t('common.disabled')}</Tag>
        ),
    },
    {
      title: t('admin.catalogActions'),
      fixed: 'right',
      width: 230,
      search: false,
      render: (_, entry) => (
        <Space size={2} wrap>
          <Button size="small" type="link" onClick={() => void openDetail(entry.id)}>
            {t('admin.catalogDetail')}
          </Button>
          <Button size="small" type="link" onClick={() => openEdit(entry)}>
            {t('common.edit')}
          </Button>
          {entry.aiStatus !== 'PUBLISHED' &&
            entry.aiStatus !== 'RETIRED' && (
              <Tooltip
                title={
                  publishBlocked(entry)
                    ? t('admin.catalogPublishBlocked')
                    : undefined
                }
              >
                <Button
                  size="small"
                  type="link"
                  disabled={publishBlocked(entry)}
                  onClick={() => void catalogAction(entry, 'publish')}
                >
                  {t('admin.catalogPublish')}
                </Button>
              </Tooltip>
            )}
          {entry.aiStatus === 'PUBLISHED' && (
            <Button
              size="small"
              type="link"
              onClick={() => void catalogAction(entry, 'hide')}
            >
              {t('admin.catalogHide')}
            </Button>
          )}
          {entry.aiStatus !== 'RETIRED' && (
            <Popconfirm
              title={t('admin.catalogRetireConfirm')}
              onConfirm={() => void catalogAction(entry, 'retire')}
            >
              <Button size="small" type="link" danger>
                {t('admin.catalogRetire')}
              </Button>
            </Popconfirm>
          )}
          {entry.aiStatus === 'RETIRED' && (
            <Button
              size="small"
              type="link"
              onClick={() => void updateCuration(entry, 'DRAFT')}
            >
              {t('admin.catalogRestore')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const detailItems = detail
    ? [
        { key: 'code', label: t('admin.catalogCode'), children: detail.code },
        {
          key: 'accession',
          label: t('admin.catalogAccession'),
          children: detail.canonicalAccession ?? '-',
        },
        { key: 'nameZh', label: t('admin.catalogNameZh'), children: detail.nameZh ?? '-' },
        { key: 'nameEn', label: t('admin.catalogNameEn'), children: detail.nameEn ?? '-' },
        {
          key: 'aliases',
          label: t('admin.catalogAliases'),
          children: detail.aliases.map((alias) => alias.alias).join('、') || '-',
          span: 2,
        },
        { key: 'species', label: t('admin.catalogSpecies'), children: detail.species },
        {
          key: 'tissue',
          label: t('admin.catalogTissueOrigin'),
          children: detail.tissueOrigin ?? '-',
        },
        {
          key: 'category',
          label: t('admin.catalogCellCategory'),
          children: detail.cellCategory
            ? t(`admin.catalogOptions.${detail.cellCategory}`)
            : '-',
        },
        {
          key: 'disease',
          label: t('admin.catalogDisease'),
          children: detail.disease ?? '-',
          span: 2,
        },
        {
          key: 'source',
          label: t('admin.catalogSource'),
          children: detail.sourceName,
        },
        {
          key: 'sourceRecord',
          label: t('admin.catalogSourceRecord'),
          children: detail.sourceRecordId ?? '-',
        },
        {
          key: 'sourceUrl',
          label: t('admin.catalogSourceUrl'),
          children: detail.sourceUrl ? (
            <a href={detail.sourceUrl} target="_blank" rel="noreferrer">
              {detail.sourceUrl}
            </a>
          ) : (
            '-'
          ),
          span: 2,
        },
        {
          key: 'sourceVersion',
          label: t('admin.catalogSourceVersion'),
          children: detail.sourceVersion ?? '-',
        },
        {
          key: 'license',
          label: t('admin.catalogLicense'),
          children: detail.license ?? '-',
        },
        {
          key: 'risk',
          label: t('admin.catalogRisk'),
          children: (
            <Tag color={detail.riskStatus === 'CLEAR' ? 'green' : 'orange'}>
              {t(`admin.catalogOptions.${detail.riskStatus}`)}
            </Tag>
          ),
        },
        {
          key: 'riskNote',
          label: t('admin.catalogRiskNote'),
          children: detail.riskNote ?? '-',
          span: 2,
        },
        {
          key: 'aiStatus',
          label: (
            <Tooltip title={t('admin.catalogAiStatusHint')}>
              <span>
                {t('admin.catalogAiStatusTitle')}
                （
                <a onClick={() => void navigate('/help?topic=catalog-ai')}>
                  {t('admin.catalogAiHelpLink')}
                </a>
                ）
              </span>
            </Tooltip>
          ),
          children: (
            <Tag color={aiStatusColor[detail.aiStatus] ?? 'default'}>
              {aiStatusLabel(detail.aiStatus)}
            </Tag>
          ),
        },
        {
          key: 'curation',
          label: t('admin.catalogCuration'),
          children: t(`admin.catalogOptions.${detail.curationStatus}`),
        },
        {
          key: 'recordStatus',
          label: t('admin.catalogRecordStatus'),
          children: t(`admin.catalogOptions.${detail.status}`),
        },
        {
          key: 'ruleVersion',
          label: t('admin.catalogRuleVersion'),
          children: detail.lastRuleVersion ?? '-',
        },
        {
          key: 'evaluatedAt',
          label: t('admin.catalogEvaluatedAt'),
          children: detail.lastEvaluatedAt
            ? new Date(detail.lastEvaluatedAt).toLocaleString()
            : '-',
        },
        {
          key: 'importedAt',
          label: t('admin.catalogImportedAt'),
          children: detail.sourceImportedAt
            ? new Date(detail.sourceImportedAt).toLocaleString()
            : '-',
        },
        {
          key: 'publishedAt',
          label: t('admin.catalogPublishedAt'),
          children: detail.publishedAt
            ? new Date(detail.publishedAt).toLocaleString()
            : '-',
        },
        {
          key: 'inputHash',
          label: t('admin.catalogInputHash'),
          children: <span style={{ wordBreak: 'break-all' }}>{detail.inputHash ?? '-'}</span>,
          span: 2,
        },
      ]
    : [];

  const cultureSourceNames = detail?.cultureSources
    ? [
        ...new Set(
          Object.values(detail.cultureSources)
            .flatMap((refs) => refs.map((ref) => ref.sourceName))
            .filter((name): name is string => Boolean(name)),
        ),
      ]
    : [];
  const hasCultureConflict = Object.values(detail?.cultureSources ?? {}).some(
    (refs) =>
      new Set(refs.map((ref) => ref.value?.trim()).filter(Boolean)).size > 1,
  );
  const activeSourceRef = activeCultureSource
    ? Object.values(detail?.cultureSources ?? {})
        .flat()
        .find(
          (ref) => ref.sourceName === activeCultureSource && ref.sourceUrl,
        )
    : Object.values(detail?.cultureSources ?? {}).flat().find((ref) => ref.sourceUrl);
  const cultureItems = CULTURE_FIELD_KEYS.map((field) => {
    const refs = detail?.cultureSources?.[field] ?? [];
    const ref = activeCultureSource
      ? refs.find((item) => item.sourceName === activeCultureSource)
      : refs[0];
    let children: React.ReactNode = `— ${t('admin.catalogCultureSourceMissing')}`;
    if (ref?.value) {
      const label =
        field === 'growthMode' || field === 'biosafetyLevel'
          ? (t(`admin.catalogOptions.${ref.value}`) ?? ref.value)
          : ref.value;
      children = activeCultureSource ? label : `${label}（${ref.sourceName}）`;
    }
    return {
      key: field,
      label: t(`admin.catalogCultureField.${field}`),
      children,
      ...(field === 'cultureConditions' ? { span: 2 } : {}),
    };
  });

  const statCards = [
    { key: 'total', value: stats?.total ?? 0, label: t('admin.catalogStatTotal') },
    { key: 'core', value: stats?.core ?? 0, label: t('admin.catalogStatCore') },
    {
      key: 'extended',
      value: stats?.extended ?? 0,
      label: t('admin.catalogStatExtended'),
    },
    {
      key: 'reviewRequired',
      value: stats?.reviewRequired ?? 0,
      label: t('admin.catalogStatReviewRequired'),
    },
    {
      key: 'retired',
      value: stats?.retired ?? 0,
      label: t('admin.catalogStatRetired'),
    },
    {
      key: 'published',
      value: stats?.published ?? 0,
      label: t('admin.catalogStatPublished'),
    },
  ];

  return (
    <section>
      {holder}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {statCards.map((item) => (
          <Col xs={12} sm={8} md={4} key={item.key}>
            <Card size="small">
              <div className="catalog-stat-value">{item.value}</div>
              <div className="catalog-stat-label">{item.label}</div>
            </Card>
          </Col>
        ))}
      </Row>
      <ProTable<CatalogEntry>
        className="cell-line-catalog-page"
        rowKey="id"
        actionRef={actionRef}
        columns={columns}
        request={requestCatalog}
        headerTitle={t('admin.cellLineCatalog')}
        search={{ labelWidth: 'auto', defaultCollapsed: false }}
        cardBordered
        options={{ reload: true, density: true, setting: true, fullScreen: true }}
        defaultSize="small"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys.map(String)),
        }}
        tableAlertOptionRender={(props) =>
          selectedRowKeys.length > 0 ? (
            <Space size="small">
              <Popconfirm
                title={t('admin.catalogBatchPublishConfirm', {
                  count: selectedRowKeys.length,
                })}
                onConfirm={() => void batchAction('publish', selectedRowKeys)}
              >
                <Button type="link" icon={<CheckOutlined />}>
                  {t('admin.catalogBatchPublish', {
                    count: selectedRowKeys.length,
                  })}
                </Button>
              </Popconfirm>
              <Popconfirm
                title={t('admin.catalogBatchRetireConfirm', {
                  count: selectedRowKeys.length,
                })}
                onConfirm={() => void batchAction('retire', selectedRowKeys)}
              >
                <Button type="link" danger icon={<StopOutlined />}>
                  {t('admin.catalogBatchRetire', {
                    count: selectedRowKeys.length,
                  })}
                </Button>
              </Popconfirm>
              <Button type="link" onClick={props.onCleanSelected}>
                {t('common.clearSelection')}
              </Button>
            </Space>
          ) : null
        }
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        scroll={{ x: 1700 }}
        toolBarRender={() => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            {t('admin.catalogCreate')}
          </Button>,
        ]}
      />
      <Modal
        open={modalOpen}
        title={editing ? t('admin.catalogEdit') : t('admin.catalogCreate')}
        width={880}
        onCancel={() => setModalOpen(false)}
        onOk={() => void form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="code"
                label={t('admin.catalogCode')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="canonicalAccession"
                label={t('admin.catalogAccession')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="catalogTier" label={t('admin.catalogTier')}>
                <Select
                  options={options(['CORE', 'EXTENDED', 'REFERENCE'], t)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="species"
                label={t('admin.catalogSpecies')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nameZh" label={t('admin.catalogNameZh')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nameEn" label={t('admin.catalogNameEn')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sourceName"
                label={t('admin.catalogSource')}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sourceRecordId"
                label={t('admin.catalogSourceRecord')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sourceUrl" label={t('admin.catalogSourceUrl')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sourceVersion"
                label={t('admin.catalogSourceVersion')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="originType" label={t('admin.catalogOriginType')}>
                <Select
                  options={options(
                    ['COMMERCIAL', 'IN_HOUSE', 'DERIVED', 'OTHER'],
                    t,
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="cellCategory"
                label={t('admin.catalogCellCategory')}
              >
                <Select
                  allowClear
                  options={options(
                    [
                      'NORMAL',
                      'TUMOR',
                      'IMMORTALIZED',
                      'PRIMARY',
                      'STEM_CELL',
                      'OTHER',
                    ],
                    t,
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tissueOrigin"
                label={t('admin.catalogTissueOrigin')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="disease" label={t('admin.catalogDisease')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="growthMode" label={t('admin.catalogGrowthMode')}>
                <Select
                  allowClear
                  options={options(
                    ['ADHERENT', 'SUSPENSION', 'SEMI_ADHERENT', 'OTHER'],
                    t,
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="riskStatus" label={t('admin.catalogRisk')}>
                <Select
                  options={options(
                    [
                      'CLEAR',
                      'REVIEW',
                      'MISIDENTIFIED',
                      'CONTAMINATION_REPORTED',
                    ],
                    t,
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="curationStatus"
                label={t('admin.catalogCuration')}
              >
                <Select
                  options={options(['DRAFT', 'APPROVED', 'RETIRED'], t)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label={t('admin.catalogRecordStatus')}>
                <Select options={options(['ACTIVE', 'INACTIVE'], t)} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="aliasesText" label={t('admin.catalogAliases')}>
                <Input.TextArea
                  rows={3}
                  placeholder={t('admin.catalogAliasesHint')}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="riskNote" label={t('admin.catalogRiskNote')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="enabledByDefault" valuePropName="checked">
                <Checkbox>{t('admin.catalogEnabledByDefault')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Drawer
        open={detailOpen}
        width={860}
        title={
          detail
            ? `${detail.code} · ${displayName(detail)}`
            : t('admin.catalogDetail')
        }
        onClose={() => setDetailOpen(false)}
      >
        {detail && (
          <>
            <Descriptions
              className="catalog-detail-descriptions"
              bordered
              size="small"
              column={2}
              labelStyle={{ width: 150 }}
              contentStyle={{ minWidth: 220 }}
              items={detailItems}
            />
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {t('admin.catalogCultureFields')}
                </span>
                <Select
                  size="small"
                  style={{ width: 180 }}
                  value={activeCultureSource ?? RECOMMENDED_CULTURE_SOURCE}
                  options={[
                    {
                      value: RECOMMENDED_CULTURE_SOURCE,
                      label: t('admin.catalogRecommendedComposite'),
                    },
                    ...cultureSourceNames.map((name) => ({
                      value: name,
                      label: name,
                    })),
                  ]}
                  onChange={(value) => {
                    if (!value || value === RECOMMENDED_CULTURE_SOURCE) {
                      setActiveCultureSource(undefined);
                    } else {
                      setActiveCultureSource(value);
                    }
                  }}
                />
                {!activeCultureSource ? (
                  <Tag color="blue">{t('admin.catalogRecommended')}</Tag>
                ) : null}
                {hasCultureConflict ? (
                  <Tag color="orange">{t('admin.catalogCultureSourceConflict')}</Tag>
                ) : null}
              </div>
              <Descriptions
                bordered
                size="small"
                column={2}
                labelStyle={{ width: 150 }}
                contentStyle={{ minWidth: 220 }}
                items={cultureItems}
              />
              {activeSourceRef ? (
                <div style={{ marginTop: 8 }}>
                  <a href={activeSourceRef.sourceUrl} target="_blank" rel="noreferrer">
                    {t('admin.catalogCultureSourceLink')}
                  </a>
                </div>
              ) : null}
            </div>
            <Tabs
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'audit',
                  label: t('admin.catalogAuditRecords'),
                  children: (
                    <Table<AuditRow>
                      size="small"
                      rowKey={(row) => `${row.createdAt}-${row.eventType}`}
                      dataSource={detail.auditRecords}
                      pagination={false}
                      columns={[
                        {
                          title: t('admin.catalogAuditEvent'),
                          dataIndex: 'eventType',
                        },
                        {
                          title: t('admin.catalogAuditTime'),
                          dataIndex: 'createdAt',
                          render: (value: string) =>
                            new Date(value).toLocaleString(),
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'versions',
                  label: t('admin.catalogVersions'),
                  children: (
                    <Table<VersionRow>
                      size="small"
                      rowKey="id"
                      dataSource={detail.versions}
                      pagination={false}
                      columns={[
                        {
                          title: t('admin.catalogVersion'),
                          dataIndex: 'version',
                          width: 70,
                        },
                        {
                          title: t('admin.catalogChangeSummary'),
                          dataIndex: 'changeSummary',
                        },
                        {
                          title: t('admin.catalogSourceVersion'),
                          dataIndex: 'sourceVersion',
                        },
                        {
                          title: t('admin.catalogAuditTime'),
                          dataIndex: 'createdAt',
                          render: (value: string) =>
                            new Date(value).toLocaleString(),
                        },
                        {
                          title: t('admin.catalogActions'),
                          width: 100,
                          render: (_, row) =>
                            detail.versions.some(
                              (item) => item.version === row.version - 1,
                            ) ? (
                              <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                  setDiffFrom(row.version - 1);
                                  setDiffTo(row.version);
                                  setDiffChanges([]);
                                  setDiffOpen(true);
                                }}
                              >
                                {t('admin.catalogViewDiff')}
                              </Button>
                            ) : null,
                        },
                      ]}
                    />
                  ),
                },
                {
                  key: 'evaluations',
                  label: t('admin.catalogEvaluations'),
                  children: (
                    <Table<EvaluationRow>
                      size="small"
                      rowKey="id"
                      dataSource={detail.evaluations}
                      pagination={false}
                      columns={[
                        {
                          title: t('admin.catalogEvaluatedAt'),
                          dataIndex: 'evaluatedAt',
                          render: (value: string) =>
                            new Date(value).toLocaleString(),
                        },
                        {
                          title: t('admin.catalogRuleVersion'),
                          dataIndex: 'ruleVersion',
                        },
                        {
                          title: t('admin.catalogAiStatusTitle'),
                          dataIndex: 'aiStatus',
                          render: (value: string) => (
                            <Tag color={aiStatusColor[value] ?? 'default'}>
                              {aiStatusLabel(value)}
                            </Tag>
                          ),
                        },
                        {
                          title: t('admin.catalogEvidenceSummary'),
                          dataIndex: ['evidence', 'evidenceSummary'],
                        },
                        {
                          title: t('admin.catalogRiskReasons'),
                          dataIndex: 'riskReasons',
                          render: (value: string[]) => value.join('；') || '-',
                        },
                        {
                          title: t('admin.catalogPublishedAllowed'),
                          dataIndex: 'publishedAllowed',
                          render: (value: boolean) =>
                            value
                              ? t('admin.catalogYes')
                              : t('admin.catalogNo'),
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Drawer>
      <Modal
        open={diffOpen}
        width={760}
        title={t('admin.catalogDiffTitle')}
        onCancel={() => setDiffOpen(false)}
        onOk={() => void showDiff()}
      >
        {detail && (
          <Space style={{ marginBottom: 12 }}>
            <Select
              style={{ width: 160 }}
              placeholder={t('admin.catalogDiffFrom')}
              value={diffFrom}
              onChange={setDiffFrom}
              options={detail.versions.map((item) => ({
                value: item.version,
                label: `v${item.version}`,
              }))}
            />
            <Select
              style={{ width: 160 }}
              placeholder={t('admin.catalogDiffTo')}
              value={diffTo}
              onChange={setDiffTo}
              options={detail.versions.map((item) => ({
                value: item.version,
                label: `v${item.version}`,
              }))}
            />
          </Space>
        )}
        <Table<DiffChange>
          size="small"
          rowKey="field"
          dataSource={diffChanges}
          pagination={false}
          columns={[
            { title: t('admin.catalogDiffField'), dataIndex: 'field' },
            { title: t('admin.catalogDiffBefore'), dataIndex: 'before' },
            { title: t('admin.catalogDiffAfter'), dataIndex: 'after' },
          ]}
          locale={{ emptyText: t('admin.catalogDiffEmpty') }}
        />
      </Modal>
    </section>
  );
}
